import React, {
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';

import Loading, {Props as LoadingProps} from '#/Loading';
import Placeholder, {Props as PlaceholderProps} from '#/Placeholder';
import {SimplePlaceholder} from '#/Placeholder/SimplePlaceholder';
import {useVariableDependencyEffect} from '_/hooks';
import {memo} from '_/utilities/memo';
import {PaginationDirection} from '_/utilities/types';

import {Props as ItemProps} from './Item';
import {
  Item,
  navigate,
  Navigation,
  NavigationMode,
  setActiveItem,
  setItems,
  setSelection,
} from './actions';
import ItemWrapper from './item-wrapper';
import {createReducer} from './reducer';
import {StyledList} from './styles';

/** @deprecated Use EList instead */
export interface NonMultiselectableProps<
  TItemType extends Item<TItemType> = Item<object>,
> {
  /**
   * This callback will be called when the active item of the list has changed, look at "selectable"
   * When the list is multi-selectable, it may be called with "undefined" (the list is allowed to have no active item),
   * otherwise it will be always called with an item
   */
  onActiveItemChange?: (arg0: TItemType) => void;
}

/** @deprecated Use EList instead */
export interface BaseProps<
  /**
   * Type of each item, in case it has more properties than the default properties of "Item" type, then these properties
   * will be accessible in the custom "ItemComponent" without any typing issues
   */
  TItemType extends Item<TItemType> = Item<object>,
  /**
   * Type of the wrapper HTML element, used to set the type of the "ref" prop, default is "div" but it may change to
   * "tbody" when used inside a table for example
   */
  TElementType extends keyof HTMLElementTagNameMap = 'div',
  /**
   * Static stuff sent to each "ItemComponent" as props
   */
  TStaticPropsType extends object = object,
> extends Omit<
    React.HTMLAttributes<HTMLElementTagNameMap[TElementType]>,
    'onDrop'
  > {
  baseElement?: TElementType;
  /**
   * Each item in the list will be rendered as an instance of this component
   * Important thing to take care of: this component should always be a ref forwarding component
   */
  ItemComponent: React.FunctionComponent<
    ItemProps<TItemType> & TStaticPropsType & {staticProps?: TStaticPropsType}
  >;
  /**
   * When the list acquires focus, if "activateOnFocus" is set, no item is already selected and "activeItem" is not set,
   * it will set selection to the first not "inactive" item in the list
   */
  activateOnFocus?: boolean;
  /**
   * Currently active item
   */
  activeItem?: TItemType;
  /**
   * Signals the "List" there are more items than the ones provided after the last provided item (lazy-loading)
   * The "List" will render a "Loading" component after the last item
   */
  hasMoreAfter?: boolean;
  /**
   * Signals the "List" there are more items than the ones provided before the last provided item (lazy-loading)
   * The "List" will render a "Loading" component before the first item
   */
  hasMoreBefore?: boolean;
  /**
   * Signals the "List" it should highlight a specific string, used to highlight search results in "SelectBox" for
   * example
   */
  highlight?: string;
  /**
   * Signals the "List" it should select items when they get hovered by mouse
   */
  hoverSelectable?: boolean;
  /**
   * A component to be rendered after above "Loading" (if any) and before the first item to provide some information to
   * user about the "List"
   */
  Info?: React.FunctionComponent;
  /**
   * Wrapper around "Info", used for "Table" for example to make it render a "tr" instead of a normal "div"
   */
  InfoComponent?: React.FunctionComponent<{
    Info: React.FunctionComponent;
    staticProps?: TStaticPropsType;
  }>;
  /**
   * List of items, each item will be rendered using either the default "ItemComponent" or the one provided in the props
   */
  items: TItemType[];
  /**
   * Will automatically scroll the viewport to the active item and focus it,this will happen only once
   */
  jumpToActiveItem?: boolean;
  /**
   * Will automatically scroll the viewport to the active item, this will happen only once
   */
  nonFocusJumpActiveItem?: boolean;
  /**
   * Automatically scrolls the viewport to the section of the active item if the active item has a section and
   * a custom section component is specified (sectionComponents). This happens only once.
   */
  scrollToActiveItemSection?: boolean;
  /**
   * Used to provide a custom loading component, the default is the "Loading" component
   */
  LoadingComponent?: React.FunctionComponent<
    LoadingProps & {staticProps?: TStaticPropsType}
  >;
  /**
   * Custom placeholder component to render when the list is empty
   */
  PlaceholderComponent?: React.FunctionComponent<
    PlaceholderProps & {staticProps?: TStaticPropsType}
  >;
  /**
   * Header of the placeholder, shown when the list is empty
   */
  placeholderHeader?: string;
  /**
   * An image rendered in the placeholder, shown when the list is empty
   */
  PlaceholderImage?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  /**
   * Optional "ref" passed to the wrapper HTML element
   */
  ref?: React.Ref<HTMLElementTagNameMap[TElementType]>;
  /**
   * A reference to the scrolling wrapper of the list provided by its parent, needed internally to preserve the position
   * of items when more items are lazy loaded before/after the current items
   */
  scrollingElementRef?: React.RefObject<HTMLElement>;
  /**
   * A mapping to render custom "ReactComponent"s for different section headers (the default renders a simple text for
   * string sections).
   * If you use custom section components together with scrollToActiveItemSection, the section components must be
   * ref forwarding.
   */
  sectionComponents?: Record<string | number, React.FunctionComponent<any>>;
  /**
   * Enforces the list to render a section component even for the first item even if it doesn't have a "section"
   * property
   */
  sectioned?: boolean;
  /**
   * Indicates the "List" is selectable (versus activate-able!) if false, the "List" will immediately call "onActivate"
   * whenever an item is selected (by pressing arrow keys for example), if true, the "List" will only call "onActivate"
   * when user presses "Enter"/"Space" keys or clicks on the item, etc.
   * The first form is useful for cases when the selection means activation (like contact list in contact module for
   * example)
   * The second form is useful for cases when activation should happen more explicitly, like selecting an item in the a
   * select box
   */
  selectable?: boolean;
  /**
   * List of selected items, the "List" will render these items differently (colors, font, etc) to visually indicate
   * these items are selected
   */
  selection?: TItemType[];
  /**
   * A JavaScript object passed to each "ItemComponent" (either default "ItemComponent" or the custom item component
   * provided in props)
   */
  staticProps?: TStaticPropsType;
  /**
   * If true, pressing "ArrowUp" on the first item will cause the last item that is not "inactive" to be selected, also
   * pressing "ArrowDown" on the last item will cause the first item that is not "inactive" to be selected
   */
  wrapped?: boolean;
  /**
   * This callback will be called when the user hits "ArrowDown" when there is no more item below the active item
   */
  onBottomTouch?: () => void;
  /**
   * This callback will be called when the user scrolls to the very last of the "List" where "Loading" component is
   * rendered on the bottom of the "List" (only if it is rendered, look at "hasMoreAfter")
   */
  onLoadMoreAfter?: () => void;
  /**
   * This callback will be called when the user scrolls to the very beginning of the "List" where "Loading" component is
   * rendered on the top of the "List" (only if it is rendered, look at "hasMoreBefore")
   */
  onLoadMoreBefore?: () => void;
  /**
   * This callback will be called when the user changes the selection
   * The selection may change by using arrow keys, ctrl-clicking/command-clicking (to select multiple items one by one),
   * shift-clicking (to select multiple items at once), using arrow keys while holding modifies and many other standard
   * ways users change selection of items in lists
   */
  onSelectionChange?: (arg0: TItemType[]) => void;
  /**
   * This callback will be called when the user hits "ArrowUp" when there is no more item above the active item
   */
  onTopTouch?: () => void;
}

const PlaceholderSelection = <TStaticPropsType extends object = object>({
  PlaceholderComponent,
  placeholderHeader,
  PlaceholderImage,
  staticProps,
}: {
  PlaceholderComponent?: React.FunctionComponent<
    PlaceholderProps & {staticProps?: TStaticPropsType}
  >;
  placeholderHeader?: string;
  PlaceholderImage?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  staticProps?: TStaticPropsType;
}) => {
  if (PlaceholderComponent) {
    return (
      <PlaceholderComponent
        placeholderHeader={placeholderHeader}
        PlaceholderImage={PlaceholderImage}
        staticProps={staticProps}
      />
    );
  }
  return PlaceholderImage ? (
    <Placeholder
      placeholderHeader={placeholderHeader}
      PlaceholderImage={PlaceholderImage}
    />
  ) : (
    <SimplePlaceholder customText={placeholderHeader} />
  );
};

/** @deprecated Use EList instead */
export type Props<
  TItemType extends Item<TItemType> = Item<object>,
  TElementType extends keyof HTMLElementTagNameMap = 'div',
  TStaticPropsType extends object = object,
> = NonMultiselectableProps<TItemType> &
  BaseProps<TItemType, TElementType, TStaticPropsType>;

/** @deprecated Use EList instead */
// eslint-disable-next-line complexity, max-statements
export function ListComponent<
  TItemType extends Item<TItemType> = Item<object>,
  TElementType extends keyof HTMLElementTagNameMap = 'div',
  TStaticPropsType extends object = object,
>(
  props_: Props<TItemType, TElementType, TStaticPropsType>,
  ref: React.Ref<HTMLElementTagNameMap[TElementType]>
): React.ReactElement {
  type BaseHTMLElement = HTMLElementTagNameMap[TElementType];

  const {
    baseElement,
    ItemComponent,
    activateOnFocus = false,
    activeItem,
    className,
    hasMoreAfter,
    hasMoreBefore,
    highlight,
    hoverSelectable,
    Info,
    InfoComponent,
    items,
    jumpToActiveItem = false,
    nonFocusJumpActiveItem = false,
    scrollToActiveItemSection = false,
    LoadingComponent,
    PlaceholderComponent,
    placeholderHeader,
    PlaceholderImage,
    scrollingElementRef: externalScrollingElementRef,
    sectionComponents,
    sectioned,
    selectable = false,
    selection,
    staticProps,
    title,
    wrapped = false,
    onActiveItemChange,
    onBlur,
    onBottomTouch,
    onFocus,
    onKeyDown,
    onLoadMoreAfter,
    onLoadMoreBefore,
    onMouseLeave,
    onSelectionChange,
    onTopTouch,
    ...props
  } = props_;

  const [state, dispatch] = useReducer(createReducer<TItemType>(), {
    activeItem,
    items,
    multiselectable: false,
    multiselecting: false,
    selectable,
    selection: selection || (activeItem ? [activeItem] : []),
    wrapped,
  });

  const internalRef = useRef<BaseHTMLElement>(null);

  const scrollingElementRef = externalScrollingElementRef || internalRef;

  const lastIncomingSelectionSync = useRef('');

  useEffect(() => {
    if (
      activeItem &&
      !activeItem.notSelectable &&
      (!selection || selection.length === 0)
    ) {
      onSelectionChange?.([activeItem]);
    }
    // deprectaed so dependency list has not been checked
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      props_.onActiveItemChange &&
      !selectable &&
      state.activeItem !== activeItem
    ) {
      if (state.activeItem) {
        props_.onActiveItemChange(state.activeItem);
      }
    }
    // deprectaed so dependency list has not been checked
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectable, state.activeItem]);
  useEffect(() => {
    if (
      state.selection
        .map(({id}) => id)
        .sort()
        .join(':') !== lastIncomingSelectionSync.current
    ) {
      onSelectionChange?.(state.selection);
    }
    // deprectaed so dependency list has not been checked
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selection]);
  useEffect(() => {
    if (state.topTouched && !hasMoreBefore) {
      onTopTouch?.();
    }
    // deprectaed so dependency list has not been checked
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.topTouched]);
  useEffect(() => {
    if (state.bottomTouched && !hasMoreAfter) {
      onBottomTouch?.();
    }
    // deprectaed so dependency list has not been checked
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.bottomTouched]);

  // Sync state with props
  useEffect(() => {
    dispatch(setItems(items));
  }, [items]);
  useEffect(() => {
    if (state.activeItem !== activeItem) {
      dispatch(setActiveItem(activeItem));
    }
    // deprectaed so dependency list has not been checked
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeItem]);
  useEffect(() => {
    lastIncomingSelectionSync.current = (selection || [])
      .map(({id}) => id)
      .sort()
      .join(':');
    if (
      selection &&
      state.selection
        .map(({id}) => id)
        .sort()
        .join(':') !== lastIncomingSelectionSync.current
    ) {
      dispatch(setSelection({newSelection: selection}));
    }
    // deprectaed so dependency list has not been checked
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection]);

  const handleNavigate = useCallback(
    (item: TItemType, mode: NavigationMode) => {
      dispatch(navigate(item, mode));
    },
    []
  );

  const activate = useCallback(
    (item: TItemType) => {
      if (state.items.includes(item)) {
        if (onActiveItemChange) {
          onActiveItemChange(item);
        } else {
          dispatch(setActiveItem(item));
        }
      }
      item.callback?.();
    },
    // deprectaed so dependency list has not been checked
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.items, state.selectable, onActiveItemChange]
  );

  const [lastDirection, setLastDirection] = useState<PaginationDirection>();
  const lastItems = useRef<TItemType[] | undefined>();
  const lastScrollFixture = useRef<
    {id: string; offsetTop: number} | undefined
  >();

  const keyDownHandler = useCallback(
    (event: React.KeyboardEvent<BaseHTMLElement>): void => {
      const {key} = event;

      if (
        !event.altKey &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.shiftKey
      ) {
        if (key === 'ArrowUp') {
          event.preventDefault();
          event.stopPropagation();
          dispatch(
            navigate<TItemType>(
              Navigation.Up,
              event.shiftKey ? NavigationMode.Selecting : NavigationMode.Normal
            )
          );
        } else if (key === 'ArrowDown') {
          event.preventDefault();
          event.stopPropagation();
          dispatch(
            navigate<TItemType>(
              Navigation.Down,
              event.shiftKey ? NavigationMode.Selecting : NavigationMode.Normal
            )
          );
        } else if (key === 'Home') {
          event.preventDefault();
          event.stopPropagation();
          dispatch(
            navigate<TItemType>(
              Navigation.Home,
              event.shiftKey ? NavigationMode.Selecting : NavigationMode.Normal
            )
          );
        } else if (key === 'End') {
          event.preventDefault();
          event.stopPropagation();
          dispatch(
            navigate<TItemType>(
              Navigation.End,
              event.shiftKey ? NavigationMode.Selecting : NavigationMode.Normal
            )
          );
        }
      }
      if (
        !event.altKey &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.shiftKey
      ) {
        if (selectable && (key === 'Enter' || key === ' ')) {
          event.stopPropagation();
          event.preventDefault();
          if (state.selection[0]) {
            activate(state.selection[0]);
          }
        }
      }

      onKeyDown?.(event);
    },
    // deprectaed so dependency list has not been checked
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activate, selectable, state.selection, items]
  );

  const refCallback = useCallback(
    (element: BaseHTMLElement | null) => {
      (internalRef as React.MutableRefObject<BaseHTMLElement | null>).current =
        element;

      /* istanbul ignore if */
      if (ref) {
        if (typeof ref === 'function') {
          /* istanbul ignore next */
          ref(internalRef.current);
        } else {
          (ref as React.MutableRefObject<BaseHTMLElement | null>).current =
            internalRef.current;
        }
      }
    },
    [ref]
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<BaseHTMLElement>): void => {
      if (
        hoverSelectable &&
        event.relatedTarget instanceof Node &&
        !internalRef.current?.contains(event.relatedTarget) &&
        event.relatedTarget !== internalRef.current
      ) {
        dispatch(
          setSelection<TItemType>({newActiveItem: undefined, newSelection: []})
        );
      }

      onBlur?.(event);
    },
    [hoverSelectable, onBlur]
  );

  // When the list acquires focus, if "activateOnFocus" is set, no item is already selected and "activeItem" is not set,
  // it will set selection to the first not "inactive" item in the list
  const handleFocus = useCallback(
    (event: React.FocusEvent<BaseHTMLElement>): void => {
      if (
        activateOnFocus &&
        !state.activeItem &&
        state.selection.length === 0
      ) {
        let firstAvailableItemIndex = 0;

        while (
          firstAvailableItemIndex < state.items.length &&
          state.items[firstAvailableItemIndex]?.inactive
        ) {
          firstAvailableItemIndex += 1;
        }

        if (
          firstAvailableItemIndex >= 0 &&
          state.items[firstAvailableItemIndex] &&
          state.activeItem !== state.items[firstAvailableItemIndex]
        ) {
          if (state.selectable) {
            dispatch(
              setSelection({
                newActiveItem: undefined,
                newSelection: [
                  state.items[firstAvailableItemIndex] as TItemType,
                ],
              })
            );
          } else {
            dispatch(setActiveItem(state.items[firstAvailableItemIndex]));
          }
        }
      }
      onFocus?.(event);
    },
    // deprectaed so dependency list has not been checked
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activateOnFocus, state.activeItem, state.selection, state.items, onFocus]
  );

  const handleMouseLeave = useCallback(
    (event: React.MouseEvent<BaseHTMLElement>) => {
      if (hoverSelectable) {
        dispatch(
          setSelection<TItemType>({newActiveItem: undefined, newSelection: []})
        );
      }

      onMouseLeave?.(event);
    },
    [hoverSelectable, onMouseLeave]
  );

  const [activeItemRef, setActiveItemRef] = useState<HTMLDivElement | null>();
  const [activeSectionRef, setActiveSectionRef] =
    useState<HTMLDivElement | null>();
  const [topLoading, setTopLoading] = useState<HTMLDivElement | null>();
  const topLoadingRef = useRef<HTMLDivElement | null>();
  const [bottomLoading, setBottomLoading] = useState<HTMLElement | null>();

  const [hasJumped, setHasJumped] = useState(false);

  useEffect(() => {
    if (
      !hasJumped &&
      (jumpToActiveItem || nonFocusJumpActiveItem) &&
      activeItemRef
    ) {
      activeItemRef.scrollIntoView({behavior: 'smooth'});
      if (!nonFocusJumpActiveItem) {
        activeItemRef.focus();
      }
      setHasJumped(true);
    }
    if (!activeItemRef && !activeItem) {
      setHasJumped(true);
    }
  }, [
    activeItem,
    activeItemRef,
    hasJumped,
    jumpToActiveItem,
    nonFocusJumpActiveItem,
  ]);

  useEffect(() => {
    if (
      !hasJumped &&
      jumpToActiveItem &&
      activeSectionRef &&
      internalRef.current &&
      scrollingElementRef.current
    ) {
      const activeSectionTop = activeSectionRef.getBoundingClientRect().top;
      const internalTop = internalRef.current.getBoundingClientRect().top;
      const yOffset = activeSectionTop - internalTop + 1;
      scrollingElementRef.current.scrollTop += yOffset;
      setHasJumped(true);
    }
  }, [
    activeSectionRef,
    hasJumped,
    internalRef,
    jumpToActiveItem,
    scrollingElementRef,
  ]);

  const activeItemRefCallback = useCallback((element: HTMLDivElement): void => {
    if (element && internalRef.current?.contains(document.activeElement)) {
      element.focus();
    }
    setActiveItemRef(element);
  }, []);

  let currentSection: string | number | {id: string} | undefined;

  useEffect(() => {
    if (process.env.NODE_ENV === 'test') {
      return (): void => undefined;
    }

    if (!bottomLoading) {
      return (): void => undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (
          entry.target === bottomLoading &&
          entry.isIntersecting &&
          scrollingElementRef.current
        ) {
          setLastDirection(PaginationDirection.Forward);
          onLoadMoreAfter?.();
        }
      });
    }, {});

    observer.observe(bottomLoading);

    return (): void => observer.unobserve(bottomLoading);
    // deprectaed so dependency list has not been checked
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bottomLoading, onLoadMoreAfter]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'test') {
      return (): void => undefined;
    }

    if (!topLoading) {
      return (): void => undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (
          entry.target === topLoading &&
          entry.isIntersecting &&
          scrollingElementRef.current
        ) {
          setLastDirection(PaginationDirection.Backward);
          onLoadMoreBefore?.();
        }
      });
    }, {});

    observer.observe(topLoading);

    return (): void => observer.unobserve(topLoading);
    // deprectaed so dependency list has not been checked
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topLoading, onLoadMoreBefore]);

  if (lastItems.current !== items) {
    lastItems.current = items;
    lastScrollFixture.current = undefined;
    if (
      lastDirection &&
      items &&
      internalRef.current &&
      scrollingElementRef.current
    ) {
      let fixture: HTMLElement | null = null;

      for (const item of lastDirection === PaginationDirection.Backward
        ? items
        : [...items].reverse()) {
        fixture = internalRef.current.querySelector(
          `:scope>[data-id="${item.id?.replace(/"/gu, '"')}"]`
        );
        if (fixture) {
          break;
        }
      }

      if (fixture && fixture.dataset.id) {
        lastScrollFixture.current = {
          id: fixture.dataset.id,
          offsetTop: fixture.getBoundingClientRect().top,
        };
      }
    }
  }

  useVariableDependencyEffect(() => {
    if (
      lastScrollFixture.current &&
      internalRef.current &&
      scrollingElementRef.current
    ) {
      const {offsetTop} = lastScrollFixture.current;
      const node: HTMLElement | null = internalRef.current.querySelector(
        `:scope>[data-id="${lastScrollFixture.current.id.replace(/"/gu, '"')}"]`
      );

      if (node) {
        scrollingElementRef.current.scrollTop -=
          offsetTop - node.getBoundingClientRect().top;
      }
    } else if (
      scrollingElementRef.current &&
      topLoadingRef.current &&
      internalRef.current
    ) {
      let offsetTop = 1;
      let node: HTMLElement = topLoadingRef.current;

      while (
        node.offsetParent instanceof HTMLElement &&
        node.offsetParent !== scrollingElementRef.current.offsetParent
      ) {
        offsetTop += node.offsetTop;
        node = node.offsetParent;
      }

      scrollingElementRef.current.scrollTop =
        node.offsetTop -
        scrollingElementRef.current.offsetTop +
        topLoadingRef.current.offsetHeight +
        offsetTop;
    }
    setLastDirection(undefined);
  }, [...items]);

  const elements = items.map((item, index) => {
    const itemProps = {
      activated: selectable
        ? state.selection.includes(item)
        : state.activeItem === item,
      hoverSelectable,
      id: item.id,
      item,
      onActivate: activate,
      role: 'option',
      selectable: state.selectable,
      staticProps,
      ...(selectable ? {onNavigate: handleNavigate} : {}),
      ...(item.title && typeof item.title === 'string'
        ? {title: item.title}
        : {}),
    };

    const shouldRenderSection =
      item.section != null && item.section !== currentSection;

    currentSection = item.section == null ? currentSection : item.section;
    const uniqueKey = item.id
      ? `${item.id}-${
          typeof item.section === 'object' ? item.section.id : item.section
        }`
      : index;
    const isActive =
      state.activeItem === item ||
      (item.id != null &&
        state.activeItem?.id === item.id &&
        item.section != null &&
        state.activeItem.section === item.section);

    const renderSection = (index === 0 && sectioned) || shouldRenderSection;
    const shouldUseSectionRefCallback =
      scrollToActiveItemSection && sectionComponents && renderSection;
    return (
      <ItemWrapper<TItemType, TStaticPropsType>
        key={uniqueKey}
        ref={
          isActive && !shouldUseSectionRefCallback
            ? activeItemRefCallback
            : undefined
        }
        highlight={highlight}
        index={index}
        ItemComponent={
          (item.ItemComponent as React.FunctionComponent<
            ItemProps<TItemType>
          >) || ItemComponent
        }
        renderSection={renderSection}
        sectionComponents={sectionComponents}
        sectionRef={
          isActive && shouldUseSectionRefCallback
            ? setActiveSectionRef
            : undefined
        }
        {...itemProps}
      />
    );
  });

  const topLoadingRefCallback = useCallback(
    (element: HTMLDivElement | null) => {
      setTopLoading(element);
      topLoadingRef.current = element;
    },
    []
  );

  const element = baseElement || ('div' as TElementType);

  return React.createElement(
    element,
    {
      'aria-label': title,
      className,
      onBlur: handleBlur,
      onFocus: handleFocus,
      onKeyDown: keyDownHandler,
      onMouseLeave: handleMouseLeave,
      ref: refCallback,
      role: 'listbox',
      tabIndex: state.selection.length > 0 ? -1 : 0,
      ...props,
    },
    hasMoreBefore &&
      (LoadingComponent ? (
        <LoadingComponent
          ref={topLoadingRefCallback}
          staticProps={staticProps}
          noDelay
        />
      ) : (
        <Loading ref={setTopLoading} noDelay />
      )),
    Info && elements.length > 0 && InfoComponent ? (
      <InfoComponent Info={Info} staticProps={staticProps} />
    ) : null,
    elements.length === 0 ? (
      <PlaceholderSelection
        PlaceholderComponent={PlaceholderComponent}
        placeholderHeader={placeholderHeader}
        PlaceholderImage={PlaceholderImage}
        staticProps={staticProps}
      />
    ) : (
      elements
    ),
    hasMoreAfter &&
      (LoadingComponent ? (
        <LoadingComponent
          ref={setBottomLoading}
          staticProps={staticProps}
          noDelay
        />
      ) : (
        <Loading ref={setBottomLoading} noDelay />
      ))
  );
}

/** @deprecated Use EList instead */
export default memo(ListComponent, StyledList, true);
